import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { ScanRoot } from "@redotech/redo-merchant-fulfillment-app/scan/root";
import { CreatePickSessionPage } from "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/create-pick-session-page";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Navigate } from "react-router-dom";
import { MerchantRouteNavigate } from "../redo-suite/merchant-routes";

const orderBreadcrumb = breadcrumbSlot();
const folderBreadcrumb = breadcrumbSlot();
const viewBreadcrumb = breadcrumbSlot();

export const scanRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Scan" },
    children: [
      {
        index: true,
        async lazy() {
          const { ScanStartPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/scan/start-page"
          );
          return { Component: ScanStartPage };
        },
      },
      {
        path: ":fulfillmentGroupId",
        async lazy() {
          const { ScanFulfillmentPage: ScanFulfillmentPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/scan/fulfillment-page"
          );
          return { Component: ScanFulfillmentPage };
        },
      },
    ],
  },
];

export const outboundLabelRoutes: PermissionedRouteObject[] = [
  {
    async lazy() {
      const { OrdersLayoutWrapper } = await import("./orders-layout-wrapper");
      return { element: <OrdersLayoutWrapper /> };
    },
    children: [
      {
        index: true,
        element: (
          <MerchantRouteNavigate
            routeTo={(routes) => routes.orderManagementDefaultRoute}
          />
        ),
      },
      {
        path: "dashboard",
        handle: {
          breadcrumb: "Dashboard",
          permission:
            OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        },
        async lazy() {
          const { DashboardPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/dashboard/dashboard"
          );
          return { element: <DashboardPage /> };
        },
      },
      {
        path: "rewards",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: { breadcrumb: "Fulfillments", breadcrumbNavigate: false },
            children: [
              {
                handle: {
                  breadcrumb: "Rewards",
                  permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
                },
                index: true,
                async lazy() {
                  const { RewardsPage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/rewards/rewards"
                  );
                  return { element: <RewardsPage /> };
                },
              },
            ],
          },
        ],
      },
      {
        path: "about",
        async lazy() {
          const { AboutOms } = await import(
            "@redotech/redo-merchant-fulfillment-app/setting/about-oms/about-oms"
          );
          return { element: <AboutOms /> };
        },
      },
      {
        path: "lookup",
        async lazy() {
          const { OutboundOrderLookup } = await import(
            "@redotech/redo-merchant-fulfillment-app/outbound-order-lookup"
          );
          return { element: <OutboundOrderLookup /> };
        },
      },
      {
        path: "bad-warehouse-bins",
        handle: {
          breadcrumb: "Bad Warehouse Bins",
          permission:
            OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        },
        async lazy() {
          const { BadWarehouseBinsPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/setting/bad-warehouse-bins"
          );
          return { element: <BadWarehouseBinsPage /> };
        },
      },
      {
        path: "admin-pick-sessions",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "Pick sessions",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { PickSessionsTablePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-sessions-table-page"
              );
              return { element: <PickSessionsTablePage /> };
            },
          },
          {
            path: ":pickSessionId",
            handle: { breadcrumb: "Active Pick Session" },
            async lazy() {
              const { ActivePickSessionPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/active-pick-session-page"
              );
              return { element: <ActivePickSessionPage /> };
            },
          },
          {
            path: ":pickSessionId/tote-scanning",
            handle: { breadcrumb: "Tote Scanning" },
            async lazy() {
              const { ToteScanningPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/tote-scanning-page"
              );
              return { element: <ToteScanningPage /> };
            },
          },
          {
            path: ":pickSessionId/route",
            handle: { breadcrumb: "Pick Route" },
            async lazy() {
              const { PickSessionRoutePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-session-route-page"
              );
              return { element: <PickSessionRoutePage /> };
            },
          },
          {
            path: "problem-order/:fulfillmentGroupId",
            handle: { breadcrumb: "Problem Order" },
            async lazy() {
              const { ProblemOrderDetailsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/problem-order-details-page"
              );
              return { element: <ProblemOrderDetailsPage /> };
            },
          },
        ],
      },
      {
        path: "pick-sessions",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "Pick sessions",
              permission: OrderManagementPermissions.MANAGE_PICK_SESSIONS,
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PickSessionsPage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-sessions-page"
                  );
                  return { element: <PickSessionsPage /> };
                },
              },
              {
                path: ":pickSessionId",
                handle: { breadcrumb: "Active Pick Session" },
                async lazy() {
                  const { ActivePickSessionPage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/active-pick-session-page"
                  );
                  return { element: <ActivePickSessionPage /> };
                },
              },
              {
                path: ":pickSessionId/tote-scanning",
                handle: { breadcrumb: "Tote Scanning" },
                async lazy() {
                  const { ToteScanningPage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/tote-scanning-page"
                  );
                  return { element: <ToteScanningPage /> };
                },
              },
              {
                path: ":pickSessionId/route",
                handle: { breadcrumb: "Pick Route" },
                async lazy() {
                  const { PickSessionRoutePage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-session-route-page"
                  );
                  return { element: <PickSessionRoutePage /> };
                },
              },
              {
                path: "problem-order/:fulfillmentGroupId",
                handle: { breadcrumb: "Problem Order" },
                async lazy() {
                  const { ProblemOrderDetailsPage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/problem-order-details-page"
                  );
                  return { element: <ProblemOrderDetailsPage /> };
                },
              },
            ],
          },
        ],
      },
      {
        path: "create-pick-session",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "Create pick session",
              permission: OrderManagementPermissions.MANAGE_PICK_SESSIONS,
            },
            index: true,
            async lazy() {
              return { element: <CreatePickSessionPage /> };
            },
          },
        ],
      },
      {
        path: "pickups",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "Carrier pickups",
              permission: OrderManagementPermissions.SCAN_AND_VERIFY,
            },
            index: true,
            async lazy() {
              const { PickupsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/common/pickups/pickups-page"
              );
              return { element: <PickupsPage /> };
            },
          },
        ],
      },
      {
        path: "batch-history",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "Batch history",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { BatchHistoryPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/batch-history/batch-history"
              );
              return { element: <BatchHistoryPage /> };
            },
          },
        ],
      },
      {
        path: "downloads",
        handle: {
          breadcrumb: "Downloads",
          handle: { permission: OrderManagementPermissions.MANAGE_SHIPMENTS },
        },
        async lazy() {
          const { ViewExportsTablePage } = await import(
            "@redotech/redo-merchant-fulfillment-app/view-exports/view-exports-table-page"
          );
          return { element: <ViewExportsTablePage /> };
        },
      },
      {
        path: "bulk-fulfillments",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: { breadcrumb: "Fulfillments", breadcrumbNavigate: false },
            children: [
              {
                handle: {
                  breadcrumb: "Bulk shipments",
                  permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { BulkFulfillmentsPage } = await import(
                        "@redotech/redo-merchant-fulfillment-app/bulk-fulfillments/bulk-fulfillments"
                      );
                      return { element: <BulkFulfillmentsPage /> };
                    },
                  },
                  {
                    path: ":batchKey",
                    handle: { breadcrumb: "Bulk shipment details" },
                    async lazy() {
                      const { BulkFulfillmentDetailsPage } = await import(
                        "@redotech/redo-merchant-fulfillment-app/bulk-fulfillments/bulk-fulfillment-details"
                      );
                      return { element: <BulkFulfillmentDetailsPage /> };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "eod-manifest",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "EOD manifest",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { EodManifestPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/end-of-day-manifest/eod-manifest-page"
              );
              return { element: <EodManifestPage /> };
            },
          },
        ],
      },
      {
        path: "packing-slips",
        handle: {
          breadcrumb: "Packing slips",
          handle: { permission: OrderManagementPermissions.MANAGE_SHIPMENTS },
        },
        children: [
          {
            index: true,
            async lazy() {
              const { PackingSlipsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slips-page"
              );
              return { element: <PackingSlipsPage /> };
            },
          },
          {
            path: ":packingSlipId",
            handle: { breadcrumb: "Packing slip builder" },
            async lazy() {
              const { PackingSlipBuilder } = await import(
                "@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slip-builder"
              );
              return { Component: PackingSlipBuilder };
            },
          },
        ],
      },
      {
        path: "personalized-slips",
        handle: {
          breadcrumb: "Personalized slips",
          handle: { permission: OrderManagementPermissions.MANAGE_SHIPMENTS },
        },
        children: [
          {
            index: true,
            async lazy() {
              const { PersonalizedSlipsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/personalized-slips/personalized-slips-page"
              );
              return { element: <PersonalizedSlipsPage /> };
            },
          },
          {
            path: ":personalizedSlipId",
            handle: { breadcrumb: "Personalized slip builder" },
            async lazy() {
              const { PersonalizedSlipsBuilderPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/personalized-slips/personalized-slip-builder"
              );
              return { Component: PersonalizedSlipsBuilderPage };
            },
          },
        ],
      },
      {
        path: "orders",
        handle: { permission: OrderManagementPermissions.VIEW_ORDER },
        children: [
          {
            handle: { breadcrumb: "All orders" },
            children: [
              {
                index: true,
                async lazy() {
                  const { Orders } = await import(
                    "./orders-table/orders-table"
                  );
                  return {
                    element: (
                      <Orders
                        filterParams={(filters) => ({
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          dateRange: filters.dateRange,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          shipmentStatus: filters.shipmentStatus,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          carrier: filters.carrier,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          destinationState: filters.destinationState,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          destinationCountry: filters.destinationCountry,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          originState: filters.originState,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          originCountry: filters.originCountry,
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          dateFilter: filters.dateFilter,
                        })}
                      />
                    ),
                  };
                },
              },
              {
                path: ":orderId",
                handle: { breadcrumb: orderBreadcrumb },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { OrderPage } = await import("./order-page");
                      return {
                        element: <OrderPage breadcrumb={orderBreadcrumb} />,
                      };
                    },
                  },
                  {
                    path: "gift-receipts",
                    handle: { breadcrumb: "Gift receipts" },
                    async lazy() {
                      const { GiftReceiptsPage } = await import(
                        "./gift-receipts"
                      );
                      return {
                        element: (
                          <GiftReceiptsPage breadcrumb={orderBreadcrumb} />
                        ),
                      };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "scan",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            Component: ScanRoot,
            handle: { permission: OrderManagementPermissions.SCAN_AND_VERIFY },
            children: scanRoutes,
          },
        ],
      },
      {
        path: "print-jobs",
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        children: [
          {
            handle: {
              breadcrumb: "Print jobs",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { PrintJobsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/printing/print-jobs"
              );
              return { element: <PrintJobsPage /> };
            },
          },
        ],
      },

      {
        path: "rate-calculator",
        handle: {
          breadcrumb: "Rate calculator",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        async lazy() {
          const { RateCalculatorPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/rate-calculator/rate-calculator-page"
          );
          return { element: <RateCalculatorPage /> };
        },
      },
      {
        path: "tariff-calculator",
        handle: {
          breadcrumb: "Tariff calculator",
          permission: [
            OrderManagementPermissions.VIEW_ORDER,
            OrderManagementPermissions.VIEW_RETURN,
            OrderManagementPermissions.MANAGE_WARRANTY,
            OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
            OrderManagementPermissions.MANAGE_SHIPMENTS,
          ],
        },
        async lazy() {
          const { TariffCalculatorPage } = await import(
            "./tariff-calculator-page"
          );
          return { element: <TariffCalculatorPage /> };
        },
      },
      { path: "", element: <Navigate replace to="awaiting-shipment" /> },
      {
        handle: { breadcrumb: "Outbound", breadcrumbNavigate: false },
        path: ":viewName",
        children: [
          {
            handle: { breadcrumb: "Fulfillments", breadcrumbNavigate: false },
            children: [
              {
                handle: {
                  breadcrumb: folderBreadcrumb,
                  breadcrumbNavigate: false,
                  permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: viewBreadcrumb },
                    async lazy() {
                      const { FulfillmentGroupPage } = await import(
                        "@redotech/redo-merchant-fulfillment-app/fulfillment-groups/fulfillment-group-page"
                      );
                      return {
                        element: (
                          <FulfillmentGroupPage
                            folderBreadcrumb={folderBreadcrumb}
                            viewBreadcrumb={viewBreadcrumb}
                          />
                        ),
                      };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const wmsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Warehouse management" },
    async lazy() {
      const { OrdersLayoutWrapper } = await import("./orders-layout-wrapper");
      return { element: <OrdersLayoutWrapper /> };
    },
    children: [
      { index: true, element: <Navigate replace to="storage-locations" /> },
      {
        path: "storage-locations",
        handle: {
          breadcrumb: "Bins",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { StorageLocationsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/storage-locations/storage-locations-page"
              );
              return { element: <StorageLocationsPage /> };
            },
          },
        ],
      },
      {
        path: "inventory-level-ledger",
        handle: {
          breadcrumb: "Inventory ledger",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { InventoryLevelLedgerPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/inventory-level-ledger/inventory-level-ledger-page"
              );
              return { element: <InventoryLevelLedgerPage /> };
            },
          },
        ],
      },
    ],
  },
];

export const backwardsCompatibleOrderRoutes: PermissionedRouteObject[] = [
  {
    path: "outbound-labels",
    element: (
      <Redirect
        match={/orders\/outbound-labels/}
        replaceValue="order-management/outbound-labels"
      />
    ),
  },
  {
    path: "batch-history",
    element: (
      <Redirect
        match={/orders\/batch-history/}
        replaceValue="order-management/outbound-labels/batch-history"
      />
    ),
  },
  {
    path: "bulk-fulfillments",
    element: (
      <Redirect
        match={/orders\/bulk-fulfillments/}
        replaceValue="order-management/outbound-labels/bulk-fulfillments"
      />
    ),
  },
  {
    path: "eod-manifest",
    element: (
      <Redirect
        match={/orders\/eod-manifest/}
        replaceValue="order-management/outbound-labels/eod-manifest"
      />
    ),
  },
  {
    path: "packing-slips",
    element: (
      <Redirect
        match={/orders\/packing-slips/}
        replaceValue="order-management/outbound-labels/packing-slips"
      />
    ),
  },
  {
    path: "personalized-slips",
    element: (
      <Redirect
        match={/orders\/personalized-slips/}
        replaceValue="order-management/outbound-labels/personalized-slips"
      />
    ),
  },
  {
    index: true,
    element: (
      <Redirect match={/orders(.*)/} replaceValue="order-management$1" />
    ),
  },
  {
    path: "outbound-labels/*",
    element: (
      <Redirect
        match={/orders\/outbound-labels(.*)/}
        replaceValue="order-management/outbound-labels$1"
      />
    ),
  },
  {
    path: "*",
    element: (
      <Redirect
        match={/orders(.*)/}
        replaceValue="order-management/outbound-labels/orders$1"
      />
    ),
  },
];
